import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { History, RefreshCw, ChevronRight } from 'lucide-react';
import clsx from 'clsx';

const fmtCas = (s: any) => {
  if (!s) return '—';
  let iso = String(s);
  if (!/[zZ]$|[+\-]\d{2}:?\d{2}$/.test(iso)) iso += 'Z'; // backend hrani UTC (utcnow)
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(s);
  const p = (n: number) => String(n).padStart(2, '0');
  return p(d.getDate()) + '.' + p(d.getMonth() + 1) + '.' + d.getFullYear() +
    ' ' + p(d.getHours()) + ':' + p(d.getMinutes());
};

const eur = (x: any) =>
  Number(x || 0).toLocaleString('sl-SI', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const VRSTE: { id: string; label: string }[] = [
  { id: '', label: 'Vse' },
  { id: 'uvoz', label: 'Uvozi' },
  { id: 'potrditev', label: 'Potrditve' },
  { id: 'urejanje', label: 'Urejanja' },
  { id: 'brisanje', label: 'Brisanja' },
  { id: 'st_sprememba', label: 'Št. dokumenta' },
  { id: 'placilo', label: 'Plačila' },
  { id: 'povezava', label: 'Povezave' },
];

const badgeCls = (v: string) => {
  const m: Record<string, string> = {
    uvoz: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    potrditev: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
    urejanje: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    brisanje: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    st_sprememba: 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300',
    placilo: 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300',
    povezava: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300',
  };
  return m[v] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
};

const vrstaLabel = (v: string) => {
  const m: Record<string, string> = {
    uvoz: 'Uvoz', potrditev: 'Potrditev', urejanje: 'Urejanje',
    brisanje: 'Brisanje', st_sprememba: 'Št. dokumenta',
    placilo: 'Plačilo', povezava: 'Povezava',
  };
  return m[v] || v || '—';
};

export function DnevnikPage() {
  const selectedCompany = useAuthStore((s: any) => s.selectedCompany);
  const baza = selectedCompany;
  const [zapisi, setZapisi] = useState<any[]>([]);
  const [vrsta, setVrsta] = useState('');
  const [limit, setLimit] = useState(100);
  const [nalagam, setNalagam] = useState(false);
  const [odprto, setOdprto] = useState<string[]>([]);
  const [iskanje, setIskanje] = useState('');

  const nalozi = () => {
    if (!baza) { setZapisi([]); return; }
    setNalagam(true);
    api.get('/dnevnik/' + baza.id, { params: { vrsta: vrsta || undefined, limit } })
      .then((r: any) => setZapisi(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Napaka pri nalaganju dnevnika'))
      .finally(() => setNalagam(false));
  };
  useEffect(nalozi, [baza, vrsta, limit]);

  const preklopi = (id: string) =>
    setOdprto((o) => o.includes(id) ? o.filter((x) => x !== id) : [...o, id]);

  const imaMeta = (z: any) =>
    z.meta && typeof z.meta === 'object' && Object.keys(z.meta).length > 0;

  const q = iskanje.trim().toLowerCase();
  const prikazani = !q ? zapisi : zapisi.filter((z: any) => [
    fmtCas(z.cas), vrstaLabel(z.vrsta), z.vrsta, z.opis, z.user_id, (z as any).uporabnik_ime, /* [PATCH dnevnik-ime v1] */
    z.meta ? JSON.stringify(z.meta) : '',
  ].some((x: any) => String(x ?? '').toLowerCase().includes(q)));

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <History className="w-6 h-6 text-blue-500" /> Dnevnik
        </h1>
        {!baza && <span className="text-sm text-gray-400">Izberite podjetje v glavi zgoraj.</span>}
      </div>

      {baza && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {VRSTE.map((v) => (
              <button key={v.id} onClick={() => setVrsta(v.id)}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                  vrsta === v.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                )}>
                {v.label}
              </button>
            ))}
            <input value={iskanje} onChange={(e) => setIskanje(e.target.value)}
              placeholder="Iskanje po vseh poljih …"
              className="flex-1 min-w-[200px] px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm" />
            <div className="ml-auto flex items-center gap-2">
              <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))}
                className="px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm">
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
              <button onClick={nalozi} title="Osveži"
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">
                <RefreshCw className={clsx('w-4 h-4', nalagam && 'animate-spin')} />
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-3 py-3 text-left whitespace-nowrap">Čas</th>
                  <th className="px-3 py-3 text-left">Vrsta</th>
                  <th className="px-3 py-3 text-left">Opis</th>
                  <th className="px-3 py-3 text-left whitespace-nowrap">Uporabnik</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {prikazani.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-400">
                    {nalagam ? 'Nalagam …' : (iskanje ? 'Ni zadetkov.' : 'Ni zapisov v dnevniku.')}
                  </td></tr>
                )}
                {prikazani.map((z) => (
                  <React.Fragment key={z.id}>
                    <tr className="border-b border-gray-100 dark:border-gray-700">
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">{fmtCas(z.cas)}</td>
                      <td className="px-3 py-2">
                        <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', badgeCls(z.vrsta))}>
                          {vrstaLabel(z.vrsta)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-900 dark:text-white">{z.opis || '—'}</td>
                      <td className={clsx('px-3 py-2 whitespace-nowrap', (z as any).uporabnik_ime
                          ? 'text-gray-700 dark:text-gray-200 text-sm'
                          : 'text-gray-500 dark:text-gray-400 font-mono text-xs')}
                        title={z.user_id || ''}>
                        {/* [PATCH dnevnik-ime v1] ime uporabnika; brez imena kratki id */}
                        {(z as any).uporabnik_ime || (z.user_id ? String(z.user_id).slice(0, 8) : '—')}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {imaMeta(z) && (
                          <button onClick={() => preklopi(z.id)}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                            <ChevronRight className={clsx('w-4 h-4 transition-transform', odprto.includes(z.id) && 'rotate-90')} />
                          </button>
                        )}
                      </td>
                    </tr>
                    {imaMeta(z) && odprto.includes(z.id) && (
                      <tr className="bg-gray-50 dark:bg-gray-900/40">
                        <td colSpan={5} className="px-6 py-3">
                          {z.vrsta === 'uvoz' ? (
                            <div className="text-xs text-gray-600 dark:text-gray-300 space-y-3">
                              <div className="flex flex-wrap gap-x-6 gap-y-1">
                                <div><span className="text-gray-400">skupaj:</span> <span className="font-medium">{z.meta.skupaj ?? '—'}</span></div>
                                <div><span className="text-gray-400">uspešno:</span> <span className="font-medium text-emerald-600 dark:text-emerald-400">{z.meta.uspesno ?? '—'}</span></div>
                                <div><span className="text-gray-400">podvojeni:</span> <span className="font-medium text-amber-600 dark:text-amber-400">{z.meta.podvojeni ?? '—'}</span></div>
                                {z.meta.izpusceni_st != null && <div><span className="text-gray-400">izpuščeni:</span> <span className="font-medium text-amber-600 dark:text-amber-400">{z.meta.izpusceni_st}</span></div>}{/* [PATCH uvoz-izpust-dnevnik v1] */}
                                <div><span className="text-gray-400">napake:</span> <span className="font-medium text-red-600 dark:text-red-400">{z.meta.napake ?? '—'}</span></div>
                              </div>
                              {Array.isArray(z.meta.uvozeni) && z.meta.uvozeni.length > 0 && (
                                <div>
                                  <div className="font-semibold text-gray-700 dark:text-gray-200 mb-1">Uvoženi ({z.meta.uvozeni.length})</div>
                                  <table className="w-full">
                                    <thead className="text-gray-400">
                                      <tr><th className="text-left font-normal pr-3 pb-1">Interna št.</th><th className="text-left font-normal pr-3 pb-1">Dobavitelj</th><th className="text-left font-normal pr-3 pb-1">Št. računa</th><th className="text-right font-normal pb-1">Znesek</th></tr>
                                    </thead>
                                    <tbody>
                                      {z.meta.uvozeni.map((u: any, i: number) => (
                                        <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                                          <td className="pr-3 py-0.5">{u.interno_st ?? '—'}</td>
                                          <td className="pr-3 py-0.5">{u.dobavitelj || '—'}</td>
                                          <td className="pr-3 py-0.5">{u.st_racuna || '—'}</td>
                                          <td className="text-right py-0.5 whitespace-nowrap">{eur(u.znesek)} €</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              {Array.isArray(z.meta.podvojeni_seznam) && z.meta.podvojeni_seznam.length > 0 && (
                                <div>
                                  <div className="font-semibold text-gray-700 dark:text-gray-200 mb-1">Neuvoženi – podvojeni ({z.meta.podvojeni_seznam.length})</div>
                                  <table className="w-full">
                                    <thead className="text-gray-400">
                                      <tr><th className="text-left font-normal pr-3 pb-1">Dobavitelj</th><th className="text-left font-normal pr-3 pb-1">Št. računa</th><th className="text-right font-normal pr-3 pb-1">Znesek</th><th className="text-left font-normal pb-1">Že pod int. št.</th></tr>
                                    </thead>
                                    <tbody>
                                      {z.meta.podvojeni_seznam.map((u: any, i: number) => (
                                        <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                                          <td className="pr-3 py-0.5">{u.dobavitelj || '—'}</td>
                                          <td className="pr-3 py-0.5">{u.st_racuna || '—'}</td>
                                          <td className="text-right pr-3 py-0.5 whitespace-nowrap">{eur(u.znesek)} €</td>
                                          <td className="py-0.5 text-amber-600 dark:text-amber-400 font-medium">{u.obstojeca_interno_st ?? '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              {/* [PATCH uvoz-izpust-dnevnik v1] izpusceni: sop zbirni vnos in direktni uvoz (ista shema) */}
                              {Array.isArray(z.meta.izpusceni) && z.meta.izpusceni.length > 0 && (
                                <div>
                                  <div className="font-semibold text-gray-700 dark:text-gray-200 mb-1">Neuvoženi – izpuščeni: predračun/ponudba ({z.meta.izpusceni.length})</div>
                                  <table className="w-full">
                                    <thead className="text-gray-400">
                                      <tr><th className="text-left font-normal pr-3 pb-1">Št. dokumenta</th><th className="text-left font-normal pb-1">Razlog</th></tr>
                                    </thead>
                                    <tbody>
                                      {z.meta.izpusceni.map((u: any, i: number) => (
                                        <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                                          <td className="pr-3 py-0.5">{u.st_racuna || '—'}</td>
                                          <td className="py-0.5">{u.razlog || u.sporocilo || '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
                              {Object.entries(z.meta).map(([k, val]) => (
                                <div key={k}>
                                  <span className="text-gray-400">{k}:</span>{' '}
                                  <span className="font-medium">{val === null || val === undefined ? '—' : String(val)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-2 text-xs text-gray-400">{iskanje ? `${prikazani.length} od ${zapisi.length}` : zapisi.length} zapisov</div>
        </>
      )}
    </div>
  );
}
